import { countItems, getOrderItemsWithInfo, getSingleOrderItem } from "@/services/OrderItems.service";
import { OrderItem, OrderItemsResponse } from "@/types";
import { activeFiltersForQueryKey } from "@/utils/activeFilters";
import { type OrderItemsHydrateOptions } from "@/utils/orderItemsIncludes";
import { timedOrdersFetch } from "@/utils/ordersPerfLog";
import { preserveReadyPreviews } from "@/utils/previewBlobs";
import { QueryClient, useQuery, useQueryClient, UseQueryOptions } from "@tanstack/vue-query";
import dayjs from "dayjs";
import { computed, MaybeRefOrGetter, toValue, unref } from "vue";

export interface FetchOrderItemsParams {
  stageId: number;
  limit?: number;
  page?: number;
  sortField?: string;
  sortOrder?: 0 | 1 | -1 | null;
  filters?: any;
  searchTerm?: string;
  hydrate?: OrderItemsHydrateOptions;
}

/** Normalize params so card and table views share the same vue-query cache entry. */
export function normalizeOrderItemsParams(params: FetchOrderItemsParams) {
  return {
    stageId: params.stageId,
    limit: params.limit ?? 25,
    page: params.page ?? 1,
    sortField: params.sortField || "createdAt",
    sortOrder: params.sortOrder == null || params.sortOrder === 0 ? -1 : params.sortOrder,
    filters: activeFiltersForQueryKey(params.filters),
    searchTerm: params.searchTerm?.trim() || undefined,
    hydrate: params.hydrate
      ? {
          includes: [...(params.hydrate.includes ?? [])].sort(),
          orderCustomFieldIds: [...(params.hydrate.orderCustomFieldIds ?? [])].sort((a, b) => a - b),
          orderItemCustomFieldIds: [...(params.hydrate.orderItemCustomFieldIds ?? [])].sort((a, b) => a - b),
        }
      : undefined,
  };
}

const formatDateForFilter = (value: any): string => {
  try {
    const startDate = dayjs(value).startOf("day").toISOString();
    const endDate = dayjs(value).endOf("day").toISOString();
    return `${startDate},${endDate}`;
  } catch {
    return value;
  }
};

const buildFilterString = (filters?: any, searchTerm?: string, entityType?: string): string => {
  let filtersString = "";

  if (filters) {
    filtersString = Object.entries(filters)
      .filter(([key, value]: [string, any]) => {
        const v = value?.value;
        const matchMode = value?.matchMode;
        if (v === undefined || (v === null && matchMode !== "isnull")) return false;
        if (typeof v === "string" && v.trim() === "") return false;
        if (Array.isArray(v)) {
          if (v.length === 0) return false;
          // BETWEEN filters are arrays but should only be applied when BOTH endpoints are set
          if (String(value?.matchMode).toLowerCase() === "between") {
            return v.length === 2 && v.every((x) => x !== undefined && x !== null && String(x).trim() !== "");
          }
        }
        return true;
      })
      .map(([key, value]: [string, any]) => {
        // if (entityType === "printJob" && key.startsWith("order.")) key = key.replace("order.", "printJob.orderItem.order.");
        if (key === "order.dueDate__isnull") return "order.dueDate__isnull";
        let matchMode = value.matchMode;
        let filterValue = value.value;
        if (key === "printerId" && filterValue && typeof filterValue === "object" && "id" in filterValue) {
          filterValue = filterValue.id;
        } else if (key === "description" && entityType === "printJob") {
          key = "orderItem.description";
        }
        switch (value.matchMode) {
          case "isnull":
            matchMode = "__isnull";
            break;
          case "notContains":
            matchMode = "__not__contains";
            filterValue = encodeURIComponent(filterValue);
            break;
          case "notEquals":
            matchMode = "__not__exact";
            filterValue = encodeURIComponent(filterValue);
            break;
          case "equals":
            matchMode = "";
            filterValue = encodeURIComponent(filterValue);
            break;
          case "dateIs":
            matchMode = "__between";
            filterValue = formatDateForFilter(filterValue);
            break;
          case "dateIsNot":
            matchMode = "__not__between";
            filterValue = formatDateForFilter(filterValue);
            break;
          case "dateBefore":
            matchMode = "__lt";
            filterValue = dayjs(filterValue[0]).toISOString();
            break;
          case "dateAfter":
            matchMode = "__gt";
            filterValue = dayjs(filterValue[1]).toISOString();
            break;
          case "in":
            matchMode = "__in";
            filterValue = filterValue.map((x: any) => encodeURIComponent(x));
            break;
          default:
            matchMode = `__${value.matchMode.toLowerCase()}`;
            break;
        }

        // PrimeVue BETWEEN filters may be arrays (e.g. [Date, Date] or [min,max]).
        // Ensure Date ranges are serialized as ISO strings for backend parsing.
        if (matchMode === "__between" && Array.isArray(filterValue) && filterValue.length === 2) {
          const [from, to] = filterValue;
          const fromStr = from instanceof Date ? dayjs(from).toISOString() : String(from);
          const toStr = to instanceof Date ? dayjs(to).toISOString() : String(to);
          filterValue = `${fromStr},${toStr}`;
        }

        return `${key}${matchMode}=${filterValue}`;
      })
      .join("&");
  }

  // Add search term to filters if provided
  if (searchTerm && searchTerm.trim()) {
    const searchFilter = `search=${encodeURIComponent(searchTerm.trim())}`;
    filtersString = filtersString ? `${filtersString}&${searchFilter}` : searchFilter;
  }

  return filtersString;
};

type OrderItemsQueryParams = { stageId?: number; page?: number };

/** Reload full rows for specific stages. Avoids refetching every column. */
export function refetchOrderItemStages(queryClient: QueryClient, stageIds: Array<number | undefined | null>) {
  const ids = new Set(stageIds.filter((id): id is number => id != null).map((id) => Number(id)));
  if (!ids.size) return;
  return queryClient.invalidateQueries({
    predicate: (query) => {
      if (query.queryKey[0] !== "orderItems") return false;
      const params = query.queryKey[1] as { stageId?: number } | undefined;
      return params?.stageId != null && ids.has(Number(params.stageId));
    },
  });
}

/** Keep list-only fields (item x/n, custom columns) when a lighter websocket row arrives. */
export function mergeOrderItemRow(current: OrderItem, updated: OrderItem): OrderItem {
  return {
    ...current,
    ...updated,
    line: updated.line ?? current.line,
    order: updated.order ? { ...current.order, ...updated.order } : current.order,
    currentFile: updated.currentFile
      ? {
          ...current.currentFile,
          ...updated.currentFile,
          preview: updated.currentFile.preview ?? current.currentFile?.preview,
          otherPreviews: updated.currentFile.otherPreviews?.length ? updated.currentFile.otherPreviews : current.currentFile?.otherPreviews,
        }
      : current.currentFile,
  };
}
export function patchOrderItemsInCache(queryClient: QueryClient, items: OrderItem[]) {
  const updates = items.filter((item) => item.id != null);
  if (!updates.length) return;

  const byId = new Map(updates.map((item) => [Number(item.id), item]));
  const queries = queryClient.getQueriesData<OrderItemsResponse>({ queryKey: ["orderItems"] });
  const inserted = new Set<string>();

  const stageOf = (item: OrderItem) => {
    const stageId = item.workflowStageId ?? item.workflowStage?.id;
    return stageId == null ? undefined : Number(stageId);
  };

  const alreadyInStage = (stageId: number, itemId: number) =>
    queries.some(([queryKey, cached]) => {
      const params = (Array.isArray(queryKey) ? queryKey[1] : undefined) as OrderItemsQueryParams | undefined;
      if (params?.stageId == null || Number(params.stageId) !== stageId) return false;
      return cached?.data?.some((item) => Number(item.id) === itemId) ?? false;
    });

  for (const [queryKey, cached] of queries) {
    if (!cached?.data) continue;
    const params = (Array.isArray(queryKey) ? queryKey[1] : undefined) as OrderItemsQueryParams | undefined;
    const stageId = params?.stageId != null ? Number(params.stageId) : undefined;
    const page = params?.page ?? 1;
    let removed = 0;
    let added = 0;
    let changed = false;

    const next = cached.data.flatMap((item) => {
      if (item.id == null || !byId.has(Number(item.id))) return [item];
      const updated = byId.get(Number(item.id))!;
      const nextStageId = stageOf(updated);
      if (stageId != null && nextStageId != null && nextStageId !== stageId) {
        removed++;
        changed = true;
        return [];
      }
      changed = true;
      return [mergeOrderItemRow(item, updated)];
    });

    if (stageId != null && page === 1) {
      for (const updated of byId.values()) {
        const nextStageId = stageOf(updated);
        if (nextStageId !== stageId || updated.id == null) continue;
        if (next.some((item) => Number(item.id) === Number(updated.id))) continue;
        if (alreadyInStage(stageId, Number(updated.id))) continue;
        const insertKey = `${stageId}:${updated.id}`;
        if (inserted.has(insertKey)) continue;
        inserted.add(insertKey);
        next.unshift(updated);
        added++;
        changed = true;
      }
    }

    if (!changed) continue;
    queryClient.setQueryData<OrderItemsResponse>(queryKey, {
      ...cached,
      data: next,
      total: Math.max(0, cached.total - removed + added),
    });
  }
}

/** Move the stage-tab badge without refetching `/orderItems/count`. */
export function shiftOrderItemCount(queryClient: QueryClient, fromStageId: number, toStageId: number) {
  if (fromStageId === toStageId) return;
  const queries = queryClient.getQueriesData<number>({ queryKey: ["orderItemsCount"] });
  for (const [queryKey, count] of queries) {
    if (typeof count !== "number") continue;
    const params = (Array.isArray(queryKey) ? queryKey[1] : undefined) as { stageId?: number } | undefined;
    if (params?.stageId == null) continue;
    const stageId = Number(params.stageId);
    if (stageId === fromStageId) queryClient.setQueryData(queryKey, Math.max(0, count - 1));
    else if (stageId === toStageId) queryClient.setQueryData(queryKey, count + 1);
  }
}

export function orderItemStageId(item?: { workflowStageId?: number | null; workflowStage?: { id?: number | null } | null }): number | undefined {
  const stageId = item?.workflowStageId ?? item?.workflowStage?.id;
  return stageId == null ? undefined : Number(stageId);
}

/** Stage ids whose cached lists still contain this row. */
export function cachedStageIdsForOrderItem(queryClient: QueryClient, itemId: number): number[] {
  const ids = queryClient
    .getQueriesData<OrderItemsResponse>({ queryKey: ["orderItems"] })
    .filter(([, cached]) => cached?.data?.some((item) => Number(item.id) === itemId))
    .map(([queryKey]) => (Array.isArray(queryKey) ? (queryKey[1] as { stageId?: number } | undefined)?.stageId : undefined))
    .filter((id): id is number => id != null)
    .map((id) => Number(id));
  return [...new Set(ids)];
}

/**
 * Move a row between cached stage lists and shift tab counts.
 * `currentStageId` is where the board already thinks the item is. When that
 * already matches the destination, counts are left alone so a local move and
 * the socket echo don't decrement twice. The list cache is still patched.
 */
export function relocateOrderItemInCache(queryClient: QueryClient, item: OrderItem, options?: { currentStageId?: number }) {
  const nextStageId = orderItemStageId(item);
  const currentStageId = options?.currentStageId != null ? Number(options.currentStageId) : undefined;
  const cachedStageIds = item.id != null ? cachedStageIdsForOrderItem(queryClient, Number(item.id)) : [];
  const staged = nextStageId != null ? { ...item, workflowStageId: nextStageId } : item;

  if (staged.id != null) patchOrderItemsInCache(queryClient, [staged]);

  const alreadyMoved = currentStageId != null && nextStageId != null && currentStageId === nextStageId;
  const cachedPrevious = cachedStageIds.find((id) => nextStageId == null || id !== nextStageId);
  const previousStageId = alreadyMoved ? undefined : (cachedPrevious ?? (currentStageId != null && currentStageId !== nextStageId ? currentStageId : undefined));
  const didMove = previousStageId != null && nextStageId != null && previousStageId !== nextStageId;
  if (didMove) shiftOrderItemCount(queryClient, previousStageId, nextStageId);

  return { previousStageId, nextStageId, didMove, cachedStageIds };
}

export function useOrders(params: MaybeRefOrGetter<FetchOrderItemsParams>, options?: Omit<UseQueryOptions<OrderItemsResponse>, "queryKey" | "queryFn">) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: computed(() => {
      const resolved = typeof params === "function" ? params() : unref(params);
      return ["orderItems", normalizeOrderItemsParams(resolved)];
    }),
    queryFn: async (ctx): Promise<OrderItemsResponse> => {
      const resolvedParams = typeof params === "function" ? params() : unref(params);
      const normalized = normalizeOrderItemsParams(resolvedParams);
      const filtersString = buildFilterString(resolvedParams.filters, resolvedParams.searchTerm);

      // Progressive: paint core rows ASAP, then replace with column-driven list hydrate.
      const listPromise = timedOrdersFetch(
        `withInfo list stage=${normalized.stageId}`,
        () => getOrderItemsWithInfo(normalized.stageId, normalized.limit, normalized.page, normalized.sortField, normalized.sortOrder, filtersString, "list", normalized.hydrate),
        { stageId: normalized.stageId, page: normalized.page, limit: normalized.limit, mode: "list" },
      );

      try {
        const core = await timedOrdersFetch(
          `withInfo core stage=${normalized.stageId}`,
          () => getOrderItemsWithInfo(normalized.stageId, normalized.limit, normalized.page, normalized.sortField, normalized.sortOrder, filtersString, "core"),
          { stageId: normalized.stageId, page: normalized.page, limit: normalized.limit, mode: "core" },
        );
        if (!ctx.signal.aborted) {
          const current = queryClient.getQueryData<OrderItemsResponse>(ctx.queryKey);
          queryClient.setQueryData(ctx.queryKey, { ...core, data: preserveReadyPreviews(current?.data, core.data) });
        }
      } catch {
        // If core fails, still wait for list.
      }

      const list = await listPromise;
      if (ctx.signal.aborted) {
        throw ctx.signal.reason ?? new DOMException("Aborted", "AbortError");
      }
      const current = queryClient.getQueryData<OrderItemsResponse>(ctx.queryKey);
      return { ...list, data: preserveReadyPreviews(current?.data, list.data) };
    },
    staleTime: 30000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    ...options,
  });

  const data = computed(() => query.data.value?.data ?? []);
  const total = computed(() => query.data.value?.total ?? 0);

  return {
    // Data properties
    data,
    total,
    // Query state
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error,
    // Query methods
    refetch: query.refetch,
    // Raw query object (for advanced usage)
    query,
  };
}

export function useOrderItem(orderItemId: MaybeRefOrGetter<number | undefined>) {
  const {
    data: orderItem,
    isLoading: loading,
    error,
  } = useQuery({
    queryKey: ["orderItem", orderItemId],
    queryFn: () => getSingleOrderItem(toValue(orderItemId) as number, "?with=printJobs,printJobs.printer,printJobs.scheme,printJobs.layout"),
    enabled: computed(() => !!toValue(orderItemId)),
  });
  return { orderItem, loading, error };
}

export function useOrderItemCount(params: MaybeRefOrGetter<FetchOrderItemsParams>) {
  const query = useQuery({
    queryKey: computed(() => {
      const resolved = typeof params === "function" ? params() : unref(params);
      return [
        "orderItemsCount",
        {
          stageId: resolved.stageId,
          filters: activeFiltersForQueryKey(resolved.filters),
          searchTerm: resolved.searchTerm?.trim() || undefined,
        },
      ];
    }),
    queryFn: async (): Promise<number> => {
      const resolvedParams = typeof params === "function" ? params() : unref(params);
      const filtersString = buildFilterString(resolvedParams.filters, resolvedParams.searchTerm);
      return await timedOrdersFetch(`count stage=${resolvedParams.stageId}`, () => countItems(resolvedParams.stageId, filtersString), {
        stageId: resolvedParams.stageId,
      });
    },
    staleTime: 30000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const count = computed(() => query.data.value);

  return { count, isFetching: query.isFetching, refetch: query.refetch };
}

// Export utility functions for reuse
export { buildFilterString, formatDateForFilter };
